<template>
  <Dropzone v-slot="{ active }" @files="addFiles">
    <div class="relative w-full h-full">
      <input
        ref="fileInputRef"
        type="file"
        class="hidden"
        :accept="acceptedFiles"
        :multiple="multiple"
        @change="onFileInputChange"
      >
      <StripedBox
        border-color="var(--color-element-neutral-100)"
        border-radius="4px"
        class="box cursor-pointer"
        :class="{ active }"
        @click.prevent.stop="emit('click')"
      >
        <div class="drop-box" :class="{ 'with-button': withButton, active }">
          <StripedBox
            :active="!value?.length"
            border-color="var(--color-element-neutral-100)"
            border-radius="4px"
            use-height
          >
            <Box v-if="value?.length" use-height>
              <div class="upload-container">
                <div
                  v-for="(upload, i) in value"
                  :key="i"
                  class="w-full flex z-0 relative"
                  :class="{
                    progressing:
                      upload && upload.isNew && upload.progress < 100 && upload.progress > 0,
                  }"
                >
                  <transition name="fade">
                    <Progress
                      v-if="upload && upload.isNew && upload.progress < 100 && upload.progress > 0"
                      :value="upload.progress"
                      class="progress"
                    />
                  </transition>
                  <div class="flex flex-1 justify-between">
                    <File :upload="upload" />
                    <div class="close-icon" @click.stop="removeFile(i)">
                      <close-icon />
                    </div>
                  </div>
                </div>
              </div>
            </Box>
            <Box
              v-else
              :aspect="0.5"
              class="placeholder"
              use-height
            >
              {{ $t('uploadFiles') }}
            </Box>
          </StripedBox>

          <custom-button variant="outlined" class="px-5" @click.stop="fileInputRef?.click()">
            {{ buttonLabel ?? $t('uploadFile') }}
          </custom-button>
        </div>
      </StripedBox>
    </div>
  </Dropzone>
</template>

<script lang="ts" setup>
import CloseIcon from '@images/remove.svg?component';
import { ref } from 'vue';
import { useVModel } from '@vueuse/core';
import File from '@components/utils/inputs/view/FileUpload.vue';
import Box from '../Box.vue';
import Progress from '../Progress.vue';
import Dropzone from '../Dropzone.vue';
import type { WbFile } from '@/entities/Entities';
import StripedBox from '../StripedBox.vue';
import { Upload, type FileUpload } from '../upload/fileUpload';
import { useDeletePrompt } from '../prompt/injects';

interface Props {
  acceptedFiles?: string;
  useHeight?: boolean;
  modelValue?: FileUpload<WbFile>[];
  withButton?: boolean;
  multiple?: boolean;
  buttonLabel?: string;
}

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'click']);
const value = useVModel(props, 'modelValue', emit, { passive: true });
const deletePrompt = useDeletePrompt();
const fileInputRef = ref<HTMLInputElement | null>(null);

const addFiles = (files: File[]) => {
  const uploads = [...(files ?? [])].map((file) => Upload.from<WbFile>(file));
  value.value = [...(value.value as FileUpload<WbFile>[]), ...uploads];
};

const onFileInputChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) {
    addFiles([...input.files]);
    input.value = '';
  }
};

const removeFile = async (fileIndex: number) => {
  if (await deletePrompt?.()) {
    value.value = value.value?.filter((_, index) => index !== fileIndex);
  }
};
</script>

<style lang="scss" scoped>
.close-icon {
  // @apply rounded-full border-2 border-white;
  @apply absolute w-9 h-9 top-0 right-0 text-white;
  @apply cursor-pointer;

  stroke-width: px(4);
  padding: px(5);

  svg {
    @apply w-full h-full;
  }
}

.progress {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2;
  @apply w-full;
  @apply h-6;
  z-index: -1;
}

.image {
  min-width: 1px;
  min-height: 1px;
}

.placeholder :deep(.box-content) {
  @apply flex justify-center items-center h-full pointer-events-none;
}

.drop-box.with-button {
  @apply flex items-end justify-between;
  @apply py-7 pl-4 pr-4.5;
  @apply gap-x-10;
  height: px(175);
}

.upload-container {
  @apply flex flex-col flex-1;
  @apply px-2.5 py-2.5;
  @apply h-full overflow-y-auto bg-table-head-gray rounded-4;

  & > * {
    @apply py-2;
    @apply border-b border-b-white;
    @apply text-neutral;

    &.progressing {
      @apply text-white;
    }

    &:last-child {
      @apply border-b-0;
    }
  }
}

:deep(.drop-box.active .box) {
  --stripe-box-border-color: var(--color-text-dark-100);
}
</style>
